import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Phone,
  Mail,
  MapPin,
  Clock,
  ArrowRight,
  Loader2,
  Instagram,
  MessageCircle,
} from "lucide-react";
import {
  COMPANY,
  OWNERS,
  buildWhatsAppMessage,
  buildWhatsAppUrl,
} from "@/lib/company";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [data, setData] = useState({
    nome: "",
    email: "",
    telefono: "",
    messaggio: "",
  });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!data.nome || !data.email || !data.messaggio) {
      toast.error("Compila i campi obbligatori");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contatti`, data);
      toast.success("Messaggio inviato. Ti risponderemo a breve.");
      setData({ nome: "", email: "", telefono: "", messaggio: "" });
    } catch (err) {
      console.error(err);
      toast.error("Errore nell'invio. Riprova più tardi.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contatti"
      data-testid="contact-section"
      className="relative bg-[#0A1120] py-28 md:py-40"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-12 lg:gap-20">
        <div className="lg:col-span-5">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-px bg-[#FF5A1F]" />
            <span className="text-xs tracking-[0.3em] uppercase text-[#FF5A1F] font-bold">
              Contatti
            </span>
          </div>
          <h2 className="headline text-white text-4xl md:text-6xl">
            Parla con{" "}
            <span className="font-display italic font-medium text-slate-300">
              i titolari.
            </span>
          </h2>
          <p className="mt-6 text-slate-400 text-base md:text-lg leading-relaxed">
            Niente call center: rispondiamo direttamente noi. Per sopralluoghi
            urgenti chiamaci o scrivici su WhatsApp.
          </p>

          {/* Titolari */}
          <div className="mt-10 grid sm:grid-cols-2 gap-px bg-[#1E293B]">
            {OWNERS.map((o) => (
              <div
                key={o.phone}
                data-testid={`owner-${o.name.split(" ")[0].toLowerCase()}`}
                className="bg-[#0A1120] p-5"
              >
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#FF5A1F] font-bold mb-2">
                  {o.role}
                </div>
                <div className="text-white font-bold text-lg leading-tight">
                  {o.name}
                </div>
                <div className="mt-4 space-y-2.5">
                  <a
                    href={`tel:${o.phoneHref}`}
                    className="flex items-center gap-2 text-slate-300 hover:text-[#FF5A1F] text-sm"
                  >
                    <Phone size={14} className="text-[#FF5A1F]" /> {o.phone}
                  </a>
                  <a
                    href={buildWhatsAppUrl(buildWhatsAppMessage(), o.phoneClean)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-slate-300 hover:text-[#25D366] text-sm"
                  >
                    <MessageCircle size={14} className="text-[#25D366]" />{" "}
                    WhatsApp
                  </a>
                </div>
              </div>
            ))}
          </div>

          {/* Altri contatti */}
          <div className="mt-px space-y-px bg-[#1E293B]">
            <a
              data-testid="contact-email"
              href={`mailto:${COMPANY.email}`}
              className="bg-[#0A1120] hover:bg-[#111C30] flex items-center gap-5 p-5 group transition-colors"
            >
              <div className="w-11 h-11 bg-[#FF5A1F] flex items-center justify-center flex-shrink-0">
                <Mail size={20} className="text-white" />
              </div>
              <div>
                <div className="text-xs tracking-[0.25em] uppercase text-slate-500 mb-1">
                  Email
                </div>
                <div className="text-white font-bold">{COMPANY.email}</div>
              </div>
            </a>
            <a
              data-testid="contact-instagram"
              href={COMPANY.instagramUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-[#0A1120] hover:bg-[#111C30] flex items-center gap-5 p-5 group transition-colors"
            >
              <div className="w-11 h-11 bg-[#FF5A1F] flex items-center justify-center flex-shrink-0">
                <Instagram size={20} className="text-white" />
              </div>
              <div>
                <div className="text-xs tracking-[0.25em] uppercase text-slate-500 mb-1">
                  Instagram
                </div>
                <div className="text-white font-bold">
                  {COMPANY.instagramHandle}
                </div>
              </div>
            </a>
            <div className="bg-[#0A1120] flex items-center gap-5 p-5">
              <div className="w-11 h-11 bg-[#FF5A1F] flex items-center justify-center flex-shrink-0">
                <MapPin size={20} className="text-white" />
              </div>
              <div>
                <div className="text-xs tracking-[0.25em] uppercase text-slate-500 mb-1">
                  Zona operativa
                </div>
                <div className="text-white font-bold">{COMPANY.area}</div>
              </div>
            </div>
            <div className="bg-[#0A1120] flex items-center gap-5 p-5">
              <div className="w-11 h-11 bg-[#FF5A1F] flex items-center justify-center flex-shrink-0">
                <Clock size={20} className="text-white" />
              </div>
              <div>
                <div className="text-xs tracking-[0.25em] uppercase text-slate-500 mb-1">
                  Orari
                </div>
                <div className="text-white font-bold">{COMPANY.hours}</div>
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          <form
            data-testid="contact-form"
            onSubmit={submit}
            className="bg-[#050B14] border border-[#1E293B] p-6 md:p-10"
          >
            <h3 className="text-2xl md:text-3xl font-bold text-white mb-2 tracking-tight">
              Scrivici direttamente
            </h3>
            <p className="text-slate-400 mb-8 text-sm">
              Risposta entro 24h lavorative.
            </p>

            <div className="grid sm:grid-cols-2 gap-3 mb-3">
              <input
                data-testid="contact-nome"
                type="text"
                placeholder="Nome e cognome*"
                value={data.nome}
                onChange={(e) => setData({ ...data, nome: e.target.value })}
                className="dea-input"
                required
              />
              <input
                data-testid="contact-email-input"
                type="email"
                placeholder="Email*"
                value={data.email}
                onChange={(e) => setData({ ...data, email: e.target.value })}
                className="dea-input"
                required
              />
            </div>
            <input
              data-testid="contact-telefono"
              type="tel"
              placeholder="Telefono"
              value={data.telefono}
              onChange={(e) => setData({ ...data, telefono: e.target.value })}
              className="dea-input mb-3"
            />
            <textarea
              data-testid="contact-messaggio"
              placeholder="Messaggio*"
              rows={6}
              value={data.messaggio}
              onChange={(e) => setData({ ...data, messaggio: e.target.value })}
              className="dea-input"
              required
            />

            <div className="flex flex-col sm:flex-row gap-3 mt-6">
              <button
                type="submit"
                data-testid="contact-submit-btn"
                disabled={loading}
                className="inline-flex items-center justify-center gap-3 bg-[#FF5A1F] hover:bg-[#EA450C] disabled:opacity-60 text-white px-7 py-4 text-sm font-semibold transition-colors flex-1"
              >
                {loading ? (
                  <>
                    <Loader2 size={18} className="animate-spin" /> Invio…
                  </>
                ) : (
                  <>
                    Invia messaggio <ArrowRight size={18} />
                  </>
                )}
              </button>
              <a
                href={buildWhatsAppUrl(buildWhatsAppMessage())}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp-btn"
                className="inline-flex items-center justify-center gap-3 bg-[#25D366] hover:bg-[#1FBA56] text-white px-7 py-4 text-sm font-semibold transition-colors"
              >
                <MessageCircle size={18} /> Scrivici su WhatsApp
              </a>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
